import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "colorado-marketing-seo-web-design-costs",
  metaTitle: "SEO & Web Design Cost in Colorado (2026 Price Guide)",
  metaDescription: "What SEO and website design cost in Colorado in 2026: websites $1,850–$5,950, SEO $950–$2,750/month, edits $250/hour. No contracts, no ad markup.",
  breadcrumbs: [{ label: "Home", to: "/" }, { label: "Learn", to: "/learn" }],
  currentLabel: "Colorado Marketing Cost Guide",
  eyebrow: "2026 Price Guide — Colorado",
  h1: (
    <>
      How Much Do <span className="text-brand-gold">SEO & Web Design</span> Cost in Colorado?
    </>
  ),
  heroDescription: "Real, published prices for websites, SEO, Google Maps, AI search and ads — so you can budget before you ever call.",
  intro: "Most agencies hide their prices until a sales call. Here are ours. A Colorado small business website costs $1,850 to $5,950 one-time with one year of hosting included. Monthly SEO and marketing costs $950 to $2,750 per month, month-to-month. Edits and repairs are $250 per hour. Ad spend is paid directly to the ad platforms with no markup.",
  definition: { term: "SEO cost", body: "What you pay each month for work that helps your business show up on Google, Google Maps and AI search. In Colorado, owner-led local SEO typically runs $950–$2,750 per month." },
  whatWeDo: [
    { title: "Starter Website — $1,850", body: "4 core pages, SEO-ready build, one year hosting. Typical delivery under 7 business days." },
    { title: "Pro Website — $2,750", body: "10 pages, foundation SEO done for you, analytics setup, one year hosting." },
    { title: "Expert Website — $5,950", body: "Up to 50 pages with service and town pages built to rank, plus advanced SEO and AI setup." },
    { title: "Marketing Starter — $950/mo", body: "Local SEO and Google Maps." },
    { title: "Marketing Growth — $1,650/mo", body: "Adds AI search visibility and social media." },
    { title: "Marketing Expert — $2,750/mo", body: "Multi-city SEO, Maps, AI, social, ads management and reputation management. Ad spend not included." },
  ],
  whyThisMarket: [
    { heading: "Cheap websites cost more later", body: "A $500 site that never ranks usually gets rebuilt within a year. Pay once for a site built to be found." },
    { heading: "No locked contracts", body: "Many agencies require 6- or 12-month terms. Our monthly plans are month-to-month." },
    { heading: "No ad markup", body: "Some agencies take 15–20% of ad spend. Your ad budget goes straight to the platforms." },
  ],
  relatedLinks: [
    { to: "/pricing", label: "Full pricing and plan builder" },
    { to: "/free-mockup", label: "Get a free website mockup" },
    { to: "/free-audit", label: "Get a free marketing audit" },
    { to: "/hire-a-website-designer-colorado", label: "Hire a website designer in Colorado" },
    { to: "/affordable-seo-small-business-colorado", label: "Affordable small business SEO" },
    { to: "/web-design-castle-rock-colorado", label: "Castle Rock web design company" },
  ],
  faqs: [
    { q: "How much does a website cost in Colorado?", a: "Our websites cost $1,850 (4 pages), $2,750 (10 pages) or $5,950 (up to 50 pages), one-time. All include one year of hosting and SSL. Typical sites launch in under 7 business days." },
    { q: "How much does SEO cost per month in Colorado?", a: "Local SEO and Google Maps starts at $950/month. Growth with AI search and social media is $1,650/month. Expert with multi-city SEO, ads and reputation management is $2,750/month." },
    { q: "Do I have to sign a long contract?", a: "No. Monthly marketing is month-to-month." },
    { q: "Is ad spend included?", a: "No. Ad spend is paid directly to the ad platforms. We never mark it up." },
    { q: "How much do website edits cost?", a: "Edits and repairs are $250 per hour." },
    { q: "How do I get a quote?", a: "Use the plan builder on the pricing page or request a free website mockup or marketing audit." },
  ],
  serviceName: "Website Design and SEO in Colorado",
  serviceDescription: "Published pricing for Colorado website design ($1,850–$5,950) and monthly SEO and marketing ($950–$2,750/month).",
  areaServed: "Colorado",
};

const URL_ = "https://coloradowebimpressions.com/colorado-marketing-seo-web-design-costs";
const offer = (name: string, price: number, desc: string, monthly = false) => ({
  "@type": "Offer", name, description: desc, price, priceCurrency: "USD", areaServed: "Colorado", url: URL_,
  ...(monthly ? { priceSpecification: { "@type": "UnitPriceSpecification", price, priceCurrency: "USD", unitCode: "MON", referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" } } } : {}),
});
const PRICE_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Article", "@id": `${URL_}#article`,
      headline: "SEO & Web Design Cost in Colorado (2026 Price Guide)",
      description: CONFIG.metaDescription, datePublished: "2026-10-17", dateModified: "2026-10-17",
      mainEntityOfPage: URL_, inLanguage: "en-US",
      author: { "@type": "Person", name: "Chris Heidlebaugh", jobTitle: "Digital Marketing Coach" },
      publisher: { "@id": "https://coloradowebimpressions.com/#organization" },
      about: ["SEO cost", "Website design cost", "Website design quote", "Colorado small business marketing"],
      spatialCoverage: { "@type": "State", name: "Colorado" },
    },
    {
      "@type": "OfferCatalog", "@id": `${URL_}#prices`, name: "Colorado Web Impressions 2026 pricing",
      provider: { "@id": "https://coloradowebimpressions.com/#organization" },
      itemListElement: [
        offer("Starter Website", 1850, "4 core pages, SEO-ready build, one year hosting, one-time price."),
        offer("Pro Website", 2750, "10 pages, foundation SEO and analytics setup, one year hosting, one-time price."),
        offer("Expert Website", 5950, "Up to 50 pages, advanced SEO and AI setup, one year hosting, one-time price."),
        offer("Marketing Starter", 950, "Local SEO and Google Maps, month-to-month.", true),
        offer("Marketing Growth", 1650, "Local SEO, Google Maps, AI search and social media, month-to-month.", true),
        offer("Marketing Expert", 2750, "Multi-city SEO, Maps, AI search, social, ads management and reputation management. Ad spend not included.", true),
        { ...offer("Edits & Repairs", 250, "Website edits and repairs, per hour."), priceSpecification: { "@type": "UnitPriceSpecification", price: 250, priceCurrency: "USD", unitCode: "HUR" } },
      ],
    },
  ],
};

export const Route = createFileRoute("/colorado-marketing-seo-web-design-costs")({
  head: () => {
    const base = buildSpokeHead(CONFIG);
    return { ...base, scripts: [...(base.scripts ?? []), { type: "application/ld+json", children: JSON.stringify(PRICE_SCHEMA) }] };
  },
  component: () => <SpokePage config={CONFIG} />,
});
